/**
 * Deduplicate assignee combo selections by user id (first occurrence wins).
 * Prevents duplicate active_assignee API errors when the same user appears twice
 * with different hidden_id values from multiple personnel combo loads.
 */
export function dedupeAssigneesById<T extends { id?: string | number | null } = any>(
  assignees: T[] | null | undefined
): T[] {
  if (!Array.isArray(assignees)) {
    return [];
  }
  const seen = new Set<string>();
  return assignees.filter((assignee) => {
    const id = assignee?.id;
    if (id == null || id === "") {
      return true;
    }
    const key = String(id);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

type SubmitAssignee = {
  id: string | number;
  name?: string;
  hidden_id?: string;
  is_partner?: boolean;
  roles?: string[];
};

function findKnownAssignee(id: string | number, knownAssignees: any[] | null | undefined) {
  if (!Array.isArray(knownAssignees)) {
    return null;
  }

  return knownAssignees.find((assignee: any) => String(assignee?.id) === String(id)) || null;
}

export function normalizeAssigneeIdsForSubmit(
  assigneeIds: any[],
  knownAssignees?: any[]
) {
  if (!Array.isArray(assigneeIds)) {
    return [];
  }

  return dedupeAssigneesById(assigneeIds
    .map((assignee: any) => {
      if (typeof assignee === "string" || typeof assignee === "number") {
        const known = findKnownAssignee(assignee, knownAssignees);
        return known?.name ? { id: assignee, name: known.name } : { id: assignee };
      }

      if (!assignee?.id) {
        return null;
      }

      const normalizedAssignee: SubmitAssignee = { id: assignee.id };
      const known = findKnownAssignee(assignee.id, knownAssignees);
      const name = assignee.name || known?.name;
      if (name !== undefined && name !== null) {
        normalizedAssignee.name = name;
      }
      if (assignee.hidden_id !== undefined && assignee.hidden_id !== null) {
        normalizedAssignee.hidden_id = assignee.hidden_id;
      }
      if (assignee.is_partner !== undefined && assignee.is_partner !== null) {
        normalizedAssignee.is_partner = assignee.is_partner;
      }
      if (assignee.roles !== undefined && assignee.roles !== null) {
        normalizedAssignee.roles = assignee.roles;
      }
      return normalizedAssignee;
    })
    .filter((assignee): assignee is SubmitAssignee => assignee !== null));
}

export function hasAssigneeWithoutName(assignees: any[]) {
  return Array.isArray(assignees) && assignees.some((assignee: any) => assignee?.id && !assignee.name);
}
